export interface ISiteMngData {
  protocolName: string;
  ctmsRowId: string;
  source: string;
  language?: string;
  optedIn?: boolean;
  status?: string;
  siteName?: string;
  address?: string;
  country?: string;
  zip?: string;
  latitude?: string;
  longitude?: string;
  siteCode?: string;
  city?: string;
  contactPhone?: string;
  piName?: string;
  contactEmail?: string;
  state?: string;
  accountNumber?: string;
  facilityType?: string;
  radiusInMiles?: number;
  priority?: number;
  threshold?: number;
  siteDirectedSchedule?: boolean;
  warmTransferNumber?: string;
  econsentUrl?: string;
  openingHour?: number;
  closingHour?: number;
  isDefaultVirtualSite?: boolean;
  allowEmails?: boolean;
  allowSmses?: boolean;
  emailCras?: string;
  studyStaffEmails?: string;
  dataVisualisationsApproved?: boolean;
  subRows: ISiteMngData[];
  uid: string; // UID is added via helper function. Will not be present in api response
  smaRowId: string;
  isSubRow?: boolean;
  eprId?: string | null;
}

export interface IStudyItem {
  studyItemId: string;
  studyUrl: string;
  studyName: string;
}

export interface IField {
  fieldValue: string;
  fieldLabel: string;
  fieldKey: string;
  itemId: string;
  isEditable: boolean;
}

export interface ISiteStatus {
  id: string;
  name: string;
  value: string;
  key: string;
  optedIn: boolean;
}

export interface ISmaLanguage {
  iso: string;
  languageName: string;
  languageId: string;
  regionalIsoCode: string;
}

export interface IInitialFilterRes {
  itemName: string;
  itemPath: string;
  itemId: string;
  eprStudyList: IStudyItem[];
  taStudyList: IStudyItem[];
  fields: IField[];
  eprFields: IField[];
  siteStatuses: ISiteStatus[];
  smaLanguageList: ISmaLanguage[];
  optedIn: boolean;
  radius: number;
  priority: number;
  threshold: number;
  warmTransferNumber: string;
  optInDateTime: string;
  isDefaultVirtualSite: boolean;
  isHybridSite: boolean;
  kiElementsEnabled: boolean;
  siteDirectedSchedule: boolean;
}

export interface IProtocoItem {
  itemID: string;
  itemName: string;
}

export interface ICountryLanguageItem {
  language: string;
  isoCode: string;
}

export interface IProtocolListRes {
  protocol?: IProtocoItem[];
  countryLanguageList?: ICountryLanguageItem[];
}

export interface IGetAvailableSitesParams {
  eligibleProtocols: string[];
  siteRootPath: string;
  language: string;
  patientState: string;
  siteSettingsId: string;
  protocolSettingsId: string;
  zipCode?: string;
  latitude?: string;
  longitude?: string;
  siteCount?: number;
}

export interface IAvailableSiteDetail {
  siteName: string;
  siteAddress: string;
  siteCity: string;
  siteState: string;
  siteZipCode: string;
  distance: number;
  siteCode: string;
  siteID: string;
  protocol: string;
  tier: number;
}

export interface IGetAvailableSitesResponse {
  patientReferralStatus: string;
  isVirtual: boolean;
  siteDetails: IAvailableSiteDetail[];
}

export const EPR_TYPE = { Global: 'global' };

// One field change inside the in-memory draft. helpers.changedFields builds this. key is not posted.
export interface IAuditChange {
  Field: string;
  // New value as it will be stored. A cleared field is the em dash "—", not an empty string.
  New: unknown;
  // Grid key used to merge edits. Omitted from the JSON sent to the API.
  key?: string;
}

// The six events rememberAudit and toAuditRequestRow store. Created, Cloned, and Translated stay sticky.
export type IAuditEventType =
  'Created' | 'Updated' | 'Cloned' | 'Translated' | 'Published' | 'CtmsFetched';

// Status on the POST row. FooterActions and Filters set this from the save or CTMS result.
export type IAuditStatus = 'Success' | 'Failed';

// Clone or translate source while it is still in memory. CamelCase here. toAuditRequestRow renames the keys to grid labels in SourceJson.
export interface IAuditSource {
  siteId: string;
  protocolName: string;
  language: string;
  siteDetailTarget: string;
}

// Pending draft in SiteManagement state, keyed by site id. Not the POST body. baseline is never sent.
export interface IAuditEntry {
  siteId: string;
  language: string;
  siteDetailTarget: string;
  protocolName: string;
  eventType: IAuditEventType;
  changes: IAuditChange[];
  source: IAuditSource | null;
  baseline: Record<string, unknown> | null;
}

// POST body for /SMA/AddSiteAuditTrails. toAuditRequestRow builds this from an IAuditEntry.
export interface IAddSiteAuditTrailRow {
  SiteID: string;
  Language: string;
  SiteDetailTarget: string;
  ProtocolName: string;
  EventType: IAuditEventType;
  TransactionId: string;
  UpdatedBy: string;
  ChangesJson: string;
  SourceJson: string | null;
  Status: IAuditStatus;
  Exception: string | null;
}

// POST body for /SMA/FetchSiteAuditTrails. fetchSiteAuditTrails fills FromDate from SMA_AUDIT_MONTHS.
export interface IFetchSiteAuditTrailsRequest {
  SiteID: string;
  Language: string;
  SiteDetailTarget: string;
  FromDate: string | null;
}

// One row after Fetch, PascalCase. normalizeSiteAuditTrailRow builds this. AuditTrailModal renders it.
export interface ISiteAuditTrailRow {
  Id: string | number;
  SiteID: string;
  Language: string;
  SiteDetailTarget: string;
  ProtocolName: string;
  EventType: string;
  Status: string | null;
  UpdatedBy: string;
  ChangesJson: string | null;
  SourceJson: string | null;
  InsertedDateTime: string;
  Exception: string | null;
  TransactionId: string;
}
